import { FC, useCallback, useEffect, useState } from 'react';
import * as yup from 'yup';
import { DocumentsService } from '../../../services/document/documentsService';
import useHandleErrors from '../../../hooks/useHandleErrors';
import useCompanies from '../../../hooks/api-calls/useCompanies';
import useDocuments from '../../../hooks/api-calls/useDocuments';
import useParks from '../../../hooks/api-calls/useParks';
import useUsers from '../../../hooks/api-calls/useUsers';
import useClients from '../../../hooks/api-calls/useClients';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import useFetch from '../../../hooks/useFetch';
import { useFormik } from 'formik';
import { toast } from 'sonner';
import { ActionIcon, Button, Modal, Text, Textarea } from 'rizzui';
import { IoClose } from 'react-icons/io5';
import CustomSelect from '../../../components/forms/CustomSelect';
import { ParkService } from '../../../services/park/parkService';
import RequiredInput from '../../../components/forms/InputHOC';
import Dropzone from '../../../components/dropzone/Dropzone';

interface DocumentsFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    refetch: () => void;
    document: string | null;
    isFromPark?: boolean;
    parkId?: string;
    userId?: string;
    isAdmin?: boolean;
    report?: boolean;
}


const schema = yup.object({
    parkId: yup.string().nullable(),
    userId: yup.string().required('El campo es requerido'),
    name: yup.string().required('El campo es requerido'),
    description: yup.string().nullable(),
    clientId: yup.string().nullable(),
    typeId: yup.string().required('El campo es requerido'),
    statusId: yup.string().nullable(),
})


interface FormValues {
    id: string;
    parkId: string | null;
    userId: string | null | undefined;
    name: string;
    description: string | null;
    clientId: string | null;
    typeId: string;
    documents?: File[] | null;
    companyId: string | null;
    statusId?: string | null;
}


const DocumentsFormModal: FC<DocumentsFormModalProps> = ({ isOpen, onClose, refetch, document, isFromPark, parkId, userId, isAdmin, report }) => {

    // STATES
    
    const service = new DocumentsService();
    const isEdit = document !== null;
    const [files, setFiles] = useState<File[]>([]);
    const [updateList, setUpdateList] = useState<boolean>(false);

    // HOOKS

    const { handleErrors } = useHandleErrors();
    const { getCompaniesList } = useCompanies();
    const { getDocumentTypesList, getDocumentStatusList } = useDocuments();
    const { getParksList, updateParksHookFilters } = useParks();
    const { getUsersList } = useUsers();
    const { getClientsList } = useClients();
    const { userCan } = usePrivilege();

    // DATA FETCHING

    const [ data, loading, error, refetchData ] = useFetch(useCallback(async () => {
        if (!document) return null;
        const response = await service.getDocumentById(document);
        return response.getResponseData();
    }, [document]));



    // FORMIK

    const formik = useFormik<FormValues>({
        initialValues: {
            id: '',
            parkId: parkId ? parkId : '',
            userId: isAdmin ? '' : userId,
            name: '',
            description: '',
            clientId: '',
            typeId: '',
            companyId: '',
            statusId: '',
        },
        validationSchema: schema,
        validateOnBlur: true,
        onSubmit: async (values) => {
            if (isEdit) {
                handleEdit(values);
            } else {
                handleCreate(values);
            }
        }
    });

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA SUBIDA DE LOS NUEVOS DOCUMENTOS
     * @EN HANDLES THE CREATION OF A NEW STOP
     * 
     * @param values
     */
    //-----------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: FormValues) => {
        try {
            const formData = new FormData();

            Object.entries(values).forEach(([key, value] : any) => {
                if (value !== null && value !== "" && !['documents'].includes(key)) {
                  formData.append(key, value);
                }
            });


            files.forEach((file, index) => {
                formData.append(`documents[${index}]`, file); 
            });

            const response = await (await service.uploadUserHasDocument(formData)).getResponseData();

            if( response.success ) {
                refetch();
                onClose();
                formik.resetForm();
                setUpdateList(!updateList);
                toast.success('Documentos subidos correctamente');
            }else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    }
    //-----------------------------------------------------------------------------------------------------------------


    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES EDITAR UN DOCUMENTO
     * @EN EDIT A DOCUMENT
     * 
     * @param values 
     */
    //--------------------------------------------------------------------------------------------------------------
    const handleEdit = async (values: any) => {
        try {

            const formData = new FormData();

            Object.entries(values).forEach(([key, value] : any) => {
                if (value !== null && value !== "" && !['documents'].includes(key)) {
                  formData.append(key, value);
                }
            });

            const response = await (await service.editDocument(formData)).getResponseData();
            if( response.success ) {
                refetch();
                onClose();
                formik.resetForm();
                toast.success('Documento editado correctamente');
            }else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    }
    //--------------------------------------------------------------------------------------------------------------


    // USE EFFECTS

    useEffect(() => {
        if (isEdit) {
            formik.setFieldValue('id', data?.id);
            formik.setFieldValue('name', data?.name);
            formik.setFieldValue('description', data?.description);
            if(!isFromPark) 
            {
                formik.setFieldValue('clientId', data?.client?.id );
            }
            formik.setFieldValue('typeId', data?.documentType?.id);
            formik.setFieldValue('companyId', data?.company?.id);
            formik.setFieldValue('userId', data?.user?.id);
            formik.setFieldValue('parkId', data?.park?.id);
            formik.setFieldValue('statusId', data?.status?.id);
        } else {
            formik.resetForm();
        }
    }, [data]);

    //---------------------------------------------------------------------------------------------------------------
    /**
     * @ES USE EFFECT PARA OBTENER LOS DATOS DEL PARQUE Y ASIGNAR EL CLIENTE AL FORMULARIO
     * @EN USE EFFECT TO GET THE PARK DATA AND ASSIGN THE CLIENT TO THE FORM
     */
    //---------------------------------------------------------------------------------------------------------------
    useEffect(() => {
        const fetchParkData = async () => {
            if (parkId) {
                try {
                    const response = await new ParkService().getParkById(parkId);
                    const parkData = await response.getResponseData();
                    if (parkData?.data?.client?.id) {
                        formik.setFieldValue('clientId', parkData.data.client.id);
                    }
                } catch (error) {
                    handleErrors(error);
                }
            }
        };
        if (!isOpen) return;
        fetchParkData();
    }, [isFromPark,isOpen]);
    //---------------------------------------------------------------------------------------------------------------


    return (
        <Modal
            isOpen={isOpen}
            onClose={() => {
                formik.resetForm();
                onClose();
            }}
            size='xl'
            overlayClassName='backdrop-blur'
            containerClassName='!max-w-[800px] !shadow-xl'
            className='z-[9999] [&_.pointer-events-none]:overflow-visible'
        >
            <div className='m-auto px-7 pt-6 pb-4'>
                <div className='mb-7 flex items-center justify-between'>
                    <Text className="text-xl">{isEdit ? 'Editar documento ' : 'Subir Documentos '}</Text>
                    <ActionIcon size='sm' variant='text' onClick={() => {
                        formik.resetForm();
                        onClose();
                    }}>
                        <IoClose className='h-auto w-6' strokeWidth={1.8} />
                    </ActionIcon>
                </div>



                <CustomSelect
                    isSearchable
                    id={'status'}
                    label="Estado"
                    options={getDocumentStatusList()}
                    onChange={(e: any) => { 
                        formik.setFieldValue('statusId', e.value)
                    }}
                    value={
                        getDocumentStatusList().filter((status: any) => status.value === formik.values.statusId)
                    }
                    required
                    error={formik.errors.statusId && formik.touched.statusId ? formik.errors.statusId : undefined}
                    display={data?.status.name !== 'Eliminado' && isEdit}
                />


                <div className="grid md:grid-cols-2 grid-cols-1 gap-2 mb-4 mt-3">
                    <RequiredInput
                        id={'name'}
                        label="Nombre"
                        placeholder="Nombre del documento"
                        value={formik.values.name}
                        onChange={(e: any) => {
                            formik.setFieldValue('name', e.target.value);
                        }}
                        error={formik.errors.name && formik.touched.name ? formik.errors.name : undefined}
                        required
                    />

                    <CustomSelect
                        isSearchable
                        id={'documentType'}
                        label="Categoría"
                        options={getDocumentTypesList()}
                        onChange={(e: any) => { 
                            formik.setFieldValue('typeId', e.value)
                        }}
                        value={
                            getDocumentTypesList().filter((type: any) => type.value === formik.values.typeId)
                        }
                        error={formik.errors.typeId && formik.touched.typeId ? formik.errors.typeId : undefined}
                        required
                        isDisabled={report}
                    />

                </div>


                <div className={`grid ${isAdmin ? 'md:grid-cols-3' : 'md:grid-cols-2'} grid-cols-1 gap-2`} >

                    <CustomSelect
                        isSearchable
                        id={'client'}
                        label="Cliente"
                        options={getClientsList()}
                        onChange={(e: any) => { 
                            updateParksHookFilters({
                                limit: 999999, 
                                filter_filters: { 
                                    client: e.value,
                                 } 
                             })
                            formik.setFieldValue('clientId', e.value)

                        }}
                        value={
                            getClientsList().filter((client: any) => client.value === formik.values.clientId)
                        }
                        error={formik.errors.parkId && formik.touched.parkId ? formik.errors.parkId : undefined}
                        isDisabled={isFromPark || report}
                    />

                    <CustomSelect
                        isSearchable
                        id={'park'}
                        label="Parque Eólico"
                        isDisabled={isFromPark || report}
                        options={getParksList()}
                        onChange={(e: any) => { 
                            formik.setFieldValue('parkId', e.value)
                        }}
                        value={
                            getParksList().filter((park: any) => park.value === formik.values.parkId)
                        }
                        error={formik.errors.parkId && formik.touched.parkId ? formik.errors.parkId : undefined}
                    />

                    <CustomSelect
                        isSearchable
                        id={'user'}
                        label="Usuario"
                        options={getUsersList()}
                        onChange={(e: any) => { 
                            formik.setFieldValue('userId', e.value)
                        }}
                        value={
                            getUsersList().filter((user: any) => user.value === formik.values.userId)
                        }
                        error={formik.errors.userId && formik.touched.userId ? formik.errors.userId : undefined}
                        display={isAdmin}
                        isDisabled={!isAdmin}
                        required
                    />
                </div>

                <div className="grid md:grid-cols-1 grid-cols-1 gap-2 mb-4 mt-3">

                    <CustomSelect
                        isSearchable
                        id={'company'}
                        label="Compañia"
                        options={getCompaniesList()}
                        onChange={(e: any) => { 
                            formik.setFieldValue('companyId', e.value)
                        }}
                        value={
                            getCompaniesList().filter((company: any) => company.value === formik.values.companyId)
                        }
                        required
                        error={formik.errors.companyId && formik.touched.companyId ? formik.errors.companyId : undefined}
                        display={userCan('list', 'companies')}
                    />


                    <Textarea
                        id='description'
                        rows={6}
                        required
                        value={formik.values.description || ''}
                        onChange={(e) => formik.setFieldValue('description', e.target.value)}
                        className={formik.errors.description && formik.touched.description ? 'invalid-form-input' : ''}
                    />

                    {!isEdit ? <Dropzone
                        id={''}
                        entity="documents"
                        setUpdateList={setUpdateList}
                        setFiles={setFiles}
                        showButton={false}
                    /> : null}
                </div>
            </div>

            <div className='flex justify-center p-7'>
                <Button
                    onClick={() => formik.handleSubmit()}
                    color='primary'
                    disabled={loading}
                >
                    {report ? loading ? 'Editando informe... ' : 'Editar informe' : loading ? 'Subiendo...' : isEdit ? 'Editar documento' : 'Subir documentos'}
                </Button>
            </div>

        </Modal>
    )

}
export default DocumentsFormModal;
